import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { z } from 'zod'

// Components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Textarea } from '@/components/elements/textarea'
import { MultiSelect } from '@/components/MultiSelect'
import { RadioGroup } from '@/components/RadioGroup'
import { SlidePanel } from '@/components/SlidePanel'
// Service & Utils
import { assignStudentCertificate } from '@/services/entity_student_certificates.service'
import { getStudents } from '@/services/entity_students.service'
import { SHOW_ALTERNATE_COURSES } from '@/utils/constants'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

const AddStudentToCertificateModal = ({ certificate, setOpen, assignedStudents }) => {
  // Context
  const { entityId } = useParams()

  // State
  const [keepOpen, setKeepOpen] = useState(false)

  // Validation Schema
  const AssignSchema = z
    .object({
      emails: z.string().optional(),
      existingStudentIds: z.array(z.string()).optional(),
      courses: z.array(
        z.object({
          requirementId: z.string(),
          assignedCourseId: z.string(),
        }),
      ),
    })
    .refine(
      (data) => {
        const hasEmails = data.emails && data.emails.trim().length > 0
        const hasExistingStudents = data.existingStudentIds && data.existingStudentIds.length > 0
        return hasEmails || hasExistingStudents
      },
      {
        message: 'Please select existing students or enter at least one email address',
        path: ['emails'],
      },
    )
    .refine(
      (data) => {
        if (!data.emails || data.emails.trim().length === 0) return true
        const emailList = data.emails.split(',').map((e) => e.trim())
        return emailList.every((email) => z.string().email().safeParse(email).success)
      },
      {
        message: 'All emails must be valid',
        path: ['emails'],
      },
    )
  /** @type {z.infer<typeof AssignSchema>} */
  const defaultValues = { emails: '', existingStudentIds: [], courses: [] }
  defaultValues.courses = certificate?.courseRequirements?.map((req) => ({
    requirementId: req.id,
    assignedCourseId: req.primaryCourse?.id || '',
  }))

  const form = useForm({
    resolver: zodResolver(AssignSchema),
    mode: 'onTouched',
    defaultValues,
  })
  const resetForm = (closeAfter = true) => {
    if (closeAfter) {
      setOpen(false)
      form.reset()
    }
    // Maintain course selections
    form.resetField('emails')
    form.resetField('existingStudentIds')
  }

  // Fetch existing students for the entity
  const { data: studentsData } = useQuery({
    queryKey: ['entity-students', entityId],
    queryFn: () => getStudents({ entityId, pagination: { pageIndex: 0, pageSize: 1000 } }),
  })

  const activeStudentIds =
    assignedStudents
      ?.filter((student) => student.status !== 'Issued')
      .map((student) => student.student.user.id) || []
  const studentOptions = (
    studentsData?.results?.map((student) => {
      const name = formatUserName(student.user)
      return {
        id: student.user?.id,
        label: `${name} (${student.user?.email})`,
      }
    }) || []
  ).filter((option) => activeStudentIds.includes(option.id) === false)

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateStudent } = useMutation({
    mutationFn: async (data) => {
      const { courses, emails, existingStudentIds } = data
      const courseIds = courses.map((c) => c.assignedCourseId)

      const requests = []

      // Process new students via email
      if (emails && emails.trim().length > 0) {
        const emailList = emails.split(',').map((e) => e.trim())
        requests.push(
          ...emailList.map((email) =>
            assignStudentCertificate({
              newUserData: { email },
              certificateId: certificate.id,
              entity: entityId,
              courses: courseIds,
            }),
          ),
        )
      }

      // Process existing students via user ID
      if (existingStudentIds && existingStudentIds.length > 0) {
        requests.push(
          ...existingStudentIds.map((userId) =>
            assignStudentCertificate({
              existingUser: userId,
              certificateId: certificate.id,
              entity: entityId,
              courses: courseIds,
            }),
          ),
        )
      }

      // Process all students concurrently
      await Promise.all(requests)

      return { totalCount: requests.length }
    },
    onError: (err) => {
      handleError(err.message)
    },
    onSuccess: (data, variables) => {
      const { closeAfter = true } = variables
      const message =
        data.totalCount === 1
          ? 'Student added successfully'
          : `${data.totalCount} students added successfully`
      handleSuccess(message)
      resetForm(closeAfter)
      queryClient.invalidateQueries({
        queryKey: ['accounts', 'instructors', 'certificates', certificate.id],
      })
    },
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => {
    setKeepOpen(false)
    await mutateStudent(data)
  }
  const onSubmitAndAddAnother = async (data) => {
    setKeepOpen(true)
    await mutateStudent({ ...data, closeAfter: false })
  }

  const courseOptions = (req) =>
    [req.primaryCourse, ...(req.alternativeCourses || [])].map((course, index) => ({
      label: `${course.name}${index === 0 ? ' (Default)' : ''}`,
      id: course.id,
    }))

  return (
    <SlidePanel
      dismissible={false}
      actions={[
        {
          label: 'Save & Add Another',
          onClick: form.handleSubmit(onSubmitAndAddAnother),
          type: 'submit',
          disabled: !form.formState.isValid || isPending,
          loading: isPending && keepOpen === true,
        },
        {
          label: `Save & Close`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
          disabled: !form.formState.isValid || isPending,
          loading: isPending && keepOpen === false,
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <section>
              <h3>Course Cost</h3>
              <p className="text-sm text-gray-500">
                Each course assignment will incur a cost of{' '}
                <strong>{`$${certificate.cost}`}</strong> per student.
              </p>
            </section>
            {SHOW_ALTERNATE_COURSES && (
              <section>
                <h3 className="text-lg">Course Module Selection</h3>
                <p className="text-sm text-gray-500">Select desired course module</p>
                <div className="grid gap-4">
                  {certificate?.courseRequirements?.map((req, index) => (
                    <>
                      <input
                        type="hidden"
                        name={`courses[${index}].requirementId`}
                        value={req.id}
                      />
                      <FormField
                        key={req.id}
                        control={form.control}
                        name={`courses[${index}].assignedCourseId`}
                        render={({ field }) => (
                          <FormItem className="rounded border p-2">
                            <FormLabel className="font-bold">
                              Course Module Requirement {index + 1}
                            </FormLabel>
                            <FormControl>
                              <RadioGroup {...field} options={courseOptions(req)} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  ))}
                </div>
              </section>
            )}
            {!SHOW_ALTERNATE_COURSES && (
              <section>
                <h3>Course Modules</h3>
                <ul className="list-inside list-disc">
                  {certificate?.courseRequirements?.map((req) => (
                    <li key={req.id} className="text-sm">
                      {req.primaryCourse.name}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <section>
              <h3 className="text-lg">Add Students</h3>
              <div className="items-top flex flex-col justify-between gap-4">
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="existingStudentIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Existing Students</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={studentOptions}
                          value={field.value || []}
                          onChange={field.onChange}
                          placeholder="Select students..."
                          width="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-muted-foreground text-sm">
                        Select one or more existing students from your entity
                      </p>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="emails"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Or Add New Student(s) by Email</FormLabel>
                      <FormControl>
                        <Textarea
                          autoComplete="off"
                          placeholder="student@example.com or student1@example.com, student2@example.com"
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-muted-foreground text-sm">
                        Enter a single email or multiple emails separated by commas
                      </p>
                    </FormItem>
                  )}
                />
              </div>
            </section>
          </form>
        </Form>
      }
      loading={isPending}
      title="Assign Course to Student"
      setOpen={setOpen}
    />
  )
}

AddStudentToCertificateModal.propTypes = {
  certificate: PropTypes.object,
  setOpen: PropTypes.func.isRequired,
  assignedStudents: PropTypes.array,
}

export default AddStudentToCertificateModal
